import React, { useEffect, useState } from "react";
import { Wallet as WalletIcon, ArrowDownCircle, ArrowUpCircle } from "lucide-react";
import PageHeader from "../../components/PageHeader";
import StatTile from "../../components/StatTile";
import { getWallet } from "../../services/archMarketplace";

const fmtINR = (n) => (n == null ? "—" : `₹${Number(n).toLocaleString("en-IN")}`);
const fmtDate = (d) => (d ? new Date(d).toLocaleString("en-IN") : "—");
const humanize = (s) => (s || "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

function ArchitectWallet() {
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    getWallet()
      .then((w) => mounted && setWallet(w))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const entries = [
    ...(wallet?.credits || []).map((c) => ({ ...c, dir: "credit" })),
    ...(wallet?.debits || []).map((d) => ({ ...d, dir: "debit" })),
  ].sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Wallet"
        subtitle="Approving a project holds its price from your balance"
        icon={WalletIcon}
        backTo="/architect/dashboard"
        backLabel="Back to projects"
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatTile icon={WalletIcon} label="Balance" value={fmtINR(wallet?.balance)} accent="flamingo" loading={loading} />
        <StatTile
          icon={ArrowDownCircle}
          label="Total Credited"
          value={fmtINR((wallet?.credits || []).reduce((s, c) => s + Number(c.amount), 0))}
          accent="emerald"
          loading={loading}
        />
        <StatTile
          icon={ArrowUpCircle}
          label="Total Held/Spent"
          value={fmtINR((wallet?.debits || []).reduce((s, d) => s + Number(d.amount), 0))}
          accent="slate"
          loading={loading}
        />
      </div>

      <div className="rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-700">
        To add funds, contact BuildVizer — top-ups are recorded by our team and reflected here.
      </div>

      <div className="rounded-lg border border-gray-200 bg-white">
        <div className="border-b px-4 py-3 text-sm font-semibold text-gray-700">Transaction History</div>
        {loading ? (
          <p className="p-4 text-sm text-gray-400">Loading…</p>
        ) : entries.length === 0 ? (
          <p className="p-4 text-sm text-gray-400">No transactions yet.</p>
        ) : (
          <div className="divide-y">
            {entries.map((e) => (
              <div key={`${e.dir}-${e.id}`} className="flex items-center justify-between px-4 py-3">
                <div>
                  <div className="text-sm font-medium text-gray-800">
                    {e.dir === "credit" ? humanize(e.kind) : `Project hold (${humanize(e.kind)})`}
                  </div>
                  <div className="text-xs text-gray-400">
                    {fmtDate(e.createdDate)} {e.reference ? `· ${e.reference}` : ""}
                  </div>
                </div>
                <div className={`text-sm font-semibold ${e.dir === "credit" ? "text-emerald-600" : "text-gray-700"}`}>
                  {e.dir === "credit" ? "+" : "−"}
                  {fmtINR(e.amount)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ArchitectWallet;
